
import React from 'react';
import { Load } from './types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Activity, Clock } from 'lucide-react';
import { getLoadIcon } from './utils/loadIcons';

interface LoadCardGridProps {
  loads: Load[];
  handleToggleStatus: (id: string) => void;
  openScheduling: (load: Load) => void;
}

const LoadCardGrid: React.FC<LoadCardGridProps> = ({ 
  loads, 
  handleToggleStatus, 
  openScheduling 
}) => {
  const getLoadConsumptionPercentage = (load: Load): number => {
    if (!load.status) return 0;
    
    const currentValue = parseInt(load.currentConsumption.replace(/[^0-9]/g, ''));
    const maxValue = parseInt(load.powerRating.replace(/[^0-9]/g, ''));
    
    return Math.min(Math.round((currentValue / maxValue) * 100), 100);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
      {loads.map((load) => (
        <Card key={`status-${load.id}`} className="shadow-sm border border-gray-200 dark:border-gray-800">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                {getLoadIcon(load.type)}
                <CardTitle className="ml-2 text-base">{load.name}</CardTitle>
              </div>
              <Switch 
                checked={load.status} 
                onCheckedChange={() => handleToggleStatus(load.id)} 
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                <span>{load.location}</span>
                <span>Max: {load.powerRating}</span>
              </div>
              
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="flex items-center">
                    <Activity className="h-4 w-4 mr-1 text-blue-500" />
                    Current Load
                  </span>
                  <span className="font-semibold">
                    {load.status ? load.currentConsumption : '0W'}
                  </span>
                </div>
                <Progress value={getLoadConsumptionPercentage(load)} className="h-2" />
              </div>
              
              <div className="flex space-x-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openScheduling(load)}
                  className="text-xs h-8 flex-1 bg-white dark:bg-transparent border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <Clock className="mr-1 h-3 w-3" />
                  {load.schedules.length > 0 ? `${load.schedules.length} Schedule(s)` : 'Add Schedule'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default LoadCardGrid;
